import { ReactElement, useContext, useMemo } from 'react';
import Image from 'next/image';
import { Heading } from '@appsumo/dorado-react';

import { CartContext } from '~/contexts/cart';
import { APPSUMO_PLUS_DEAL_ID } from '~/lib/util/constants';
import { CartItemProps } from '~/types/cart';

import {
  Deals,
  PlusUpsell,
  SavedForLater,
  Subscriptions,
  Summary,
} from '~/components/cart';
import EmptyCart from '~/components/checkout/Global/EmptyCart';
import { Link } from '~/components/global';

import arrowLeft from '~/public/font-awesome/arrow-left-solid.png';

export default function Container(): ReactElement {
  const { cart, user } = useContext(CartContext);

  // Prod, staging, and local dev have the same AppSumo Plus deal id (1735).
  // Remove plus from the cart items and put it in its own section.
  // Used an array for plusCartItem for consistency with cartItems.
  const [cartItems, plusCartItem] = useMemo(
    () =>
      (cart?.cart_items ?? []).reduce(
        (m, item) => {
          if (item.deal.id === APPSUMO_PLUS_DEAL_ID) {
            m[1].push(item);
          } else {
            m[0].push(item);
          }

          return m;
        },
        [[], []] as [CartItemProps[], CartItemProps[]],
      ),
    [cart?.cart_items],
  );

  const upperComponents =
    cart?.cart_items.length === 0 ? (
      <EmptyCart />
    ) : (
      <>
        <Subscriptions plusCartItem={plusCartItem[0]} />
        <Deals cartItems={cartItems} hasPlusInCart={!!plusCartItem[0]} />
        <PlusUpsell
          cart={cart}
          user={user}
          hasPlusInCart={!!plusCartItem[0]}
          className="hidden lg:flex"
        />
      </>
    );

  const cartUpperContent = (
    <div className="flex flex-col gap-4 lg:col-start-1 lg:col-end-1">
      {upperComponents}
      <SavedForLater className="hidden lg:block" />
    </div>
  );

  return (
    <div>
      <div className="mb-6 hidden items-center lg:flex">
        <Image src={arrowLeft} alt="arrow left" height={16} className="" />
        <Link
          href="/browse"
          className="ml-2 text-sm text-blue-600 hover:underline sm:pr-8"
        >
          Back to browse
        </Link>
      </div>
      <div>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[2fr,1fr]">
          <Heading.H1 className="mb-4">Cart</Heading.H1>
          <Heading.H3 className="mb-4 hidden self-end lg:block">
            Order summary
          </Heading.H3>
        </div>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[2fr,1fr]">
          {cartUpperContent}
          <Summary hasPlusInCart={!!plusCartItem[0]} />
        </div>
      </div>
    </div>
  );
}

export { Container };
